import React, { Component, type ErrorInfo, type ReactNode } from "react";
import GlobalErrorFallback from "./GlobalErrorFallback";
import { captureAppException, buildLogContext } from "../Utils/appLogger";
import { upgradeClaritySession } from "../analytics/clarity";

interface Props {
  children: ReactNode;
}

interface State {
  error: unknown | null;
}

class ErrorBoundaryComponent extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureAppException(
      "Uncaught React render error",
      error,
      buildLogContext(
        "app",
        "error-boundary",
        undefined,
        undefined,
        undefined,
        {
          component_stack: info.componentStack?.slice(0, 500) ?? ""
        }
      )
    );
    upgradeClaritySession("error");
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <GlobalErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

const ErrorBoundary: React.FC<Props> = ({ children }) => (
  <ErrorBoundaryComponent>{children}</ErrorBoundaryComponent>
);

export default ErrorBoundary;
